import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Stack, TextField, Tooltip, Typography, Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import { httpClient } from '../services/http-client';

interface Props {
  accountId: string;
  symbol: string;
  /** What the trade replay computed, shown for comparison. */
  replayedValue: string | number;
  /** True when a stored override is already in effect. */
  overridden?: boolean;
  currency?: string;
  onSaved: () => void;
}

const money = (v: string | number) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Records the book value a BROKER reports, where the replay cannot reach it.
 *
 * Cost base is normally replayed from trades, which is right for everything an
 * activity export actually contains. But an ETF's book value also moves on
 * notional distributions and return of capital, and brokers publish those rows
 * with no quantity, no price and no amount -- Qtrade writes five such rows for
 * XEQT and XID, leaving the replay $36.83 low. That figure is not recoverable
 * from the file by any means, so the household enters what their statement
 * says.
 *
 * The replayed value is shown alongside and stored with the override, because
 * a large gap between them usually means a TRADE IS MISSING rather than that
 * the broker disagrees -- and silently adopting the broker's number would bury
 * the more serious problem.
 */
export const BookValueEditor: React.FC<Props> = ({
  accountId, symbol, replayedValue, overridden, currency = 'CAD', onSaved,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drift = Number(value || 0) - Number(replayedValue || 0);
  const driftIsLarge =
    Number(value) > 0 && Math.abs(drift) > Math.abs(Number(replayedValue || 0)) * 0.05;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await httpClient.post(`/investments/accounts/${accountId}/book-value`, {
        symbol,
        book_value: value,
        replayed_value: String(replayedValue ?? ''),
        note,
      });
      setOpen(false);
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    try {
      await httpClient.delete(
        `/investments/accounts/${accountId}/book-value/${encodeURIComponent(symbol)}`);
      setOpen(false);
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Could not clear');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title={overridden
        ? 'Book value set from your statement. Click to change.'
        : 'Set the book value your broker reports'}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setValue(String(replayedValue ?? '')); setOpen(true); }}
          sx={{ ml: 0.5, opacity: overridden ? 1 : 0.45 }}
          color={overridden ? 'primary' : 'default'}
        >
          <EditIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Book value for {symbol}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Calculated from your trades: <strong>{currency} {money(replayedValue)}</strong>.
            An ETF&rsquo;s book value also moves on return of capital and
            reinvested distributions, which brokers report with no amount &mdash;
            so this can differ from your statement. Enter what your statement says.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Book value from your statement"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              size="small"
              autoFocus
              fullWidth
            />
            <TextField
              label="Note (optional)"
              placeholder="e.g. Qtrade statement, Aug 2026"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>

          {driftIsLarge && (
            // A large gap is far more often a missing trade than a
            // disagreeing broker, and that is worth saying before it is
            // papered over with an override.
            <Alert severity="warning" sx={{ mt: 2 }}>
              That is {money(Math.abs(drift))} away from the calculated figure.
              A gap this large usually means a purchase is missing from the
              imported history rather than a difference in the broker&rsquo;s
              accounting &mdash; worth checking before saving.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          {overridden && (
            <Button onClick={clear} disabled={busy} color="inherit">
              Use calculated
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !value} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
